import { CaseState } from 'app/area/case/types-store';
import { ComponentDescriptor, ConnectedComponentProps } from 'app/infrastructure/ui/types-uischema';
import { getEntityTypePathFromStorePath } from 'app/infrastructure/store/entity';
import { resolveVisibleSections } from 'app/infrastructure/ui/renderer/resolvers';
import { validationDescendants as selectValidationDescendants } from 'app/area/case/selectors/validation-descendants';
import { memoize } from 'app/infrastructure/js';
import { StoreItemPath } from 'app/types-shared';
import { SchemaBuilder } from 'app/infrastructure/ui/builder';
import { ValidationLevel } from 'ui';

export const findFirstSectionHostingEntity = (sections, path) => {

  const sameEntityPathAndRootLevelOrIterator =  c =>
    c.entityTypePath
    && JSON.stringify(c.entityTypePath).toLowerCase() === path
    && (c.entityTypePath.length === 1 || !!c.iterate);

  const childHavingPath = (item) => {
   return item.children && (
     item.children.find(sameEntityPathAndRootLevelOrIterator) || item.children.some(childHavingPath) );
  };
  return sections.filter(childHavingPath);
};

export const inferHostSectionOfChildEntity = ({ sections, entityTypePath }:
  { sections: ComponentDescriptor[]; entityTypePath: string[]; }) => {
  const path = JSON.stringify( entityTypePath ).toLowerCase();
  const matches = findFirstSectionHostingEntity(sections, path);
  return Array.isArray(matches) ? matches.sort( (a, b) => (a.id - b.id) )[0] : undefined;
};

export const firstSectionId = (caseState: CaseState, uiEngineProps: any) => {
  if(!caseState.uiDefinition){
    return undefined;
  }
  const availableSections = SchemaBuilder.create(caseState.renderMode, caseState.uiDefinition).availableSections;
  const visibleSections = resolveVisibleSections(availableSections, caseState, uiEngineProps);
  const section = visibleSections[0];
  return { firstSection : section.id };
};

const inferActiveSectionId = memoize((caseState: CaseState, uiEngineProps: ConnectedComponentProps, activeChildItem: StoreItemPath[]) => {
  const isDeepLink = Array.isArray(activeChildItem) && activeChildItem.length > 1;
  const availableSections = SchemaBuilder.create(caseState.renderMode, caseState.uiDefinition).availableSections;
  const visibleSections = resolveVisibleSections(availableSections, caseState, uiEngineProps);
  const resolvedSection = isDeepLink ?
                            inferHostSectionOfChildEntity(
                              { sections: visibleSections, entityTypePath: getEntityTypePathFromStorePath(activeChildItem) })
                            :
                            visibleSections[0];

  if(resolvedSection) {
    return Number(resolvedSection.id);
  }

  return undefined;

});

/**
 * Since the domain id of the default section is dynamic, it must be resolved
 * explicitly from UI definition when the url only contains caseId or draftId
 *
 * If there are validation errors in store, the section of the first validation error
 * domain parent section must be activated
 */
export const activeSectionState = (caseState: CaseState, uiEngineProps: ConnectedComponentProps) => {
  const {
    hostProps,
    invalidSections
  } = uiEngineProps;
  const {
    activeSection,
    expandAll
  } = hostProps();

  const submitFailed = invalidSections && invalidSections.length > 0;
  let sectionId = (!activeSection && submitFailed) ? invalidSections[0] : activeSection;
  // Never infer when there are pending validation errors
  if(!sectionId && caseState.uiDefinition && !submitFailed) {
    sectionId = inferActiveSectionId(caseState, uiEngineProps, hostProps().activeChildItem);
  }
  return { activeSection: sectionId, expandAll };
};

export const nextAndPreviousSection = (caseState: CaseState, uiEngineProps: ConnectedComponentProps) => {
  const { activeSection } = uiEngineProps;

  if(!activeSection || !caseState.uiDefinition){
    return { previousSection: undefined, nextSection: undefined };
  }

  const availableSections = SchemaBuilder.create(caseState.renderMode, caseState.uiDefinition).availableSections;
  const sections = resolveVisibleSections(availableSections, caseState, uiEngineProps);
  const activeIndex = sections.findIndex( c => c.id === activeSection );

  return activeIndex > -1 && sections.length > 1 ? {
    previousSection: sections[activeIndex - 1],
    nextSection: sections[activeIndex + 1]
  } : undefined;
};

export const invalidSectionIds = memoize((caseState: CaseState, uiEngineProps: ConnectedComponentProps) => {
  const havingErrors = (componentDescriptor: ComponentDescriptor) => {
    const v = selectValidationDescendants(
      caseState, { hostProps: uiEngineProps.hostProps, componentDescriptor } as any
    ).validationDescendants;
    return v && v.length && v.some(val => val.level === ValidationLevel.error);
  };
  const availableSections = SchemaBuilder.create(caseState.renderMode, caseState.uiDefinition).availableSections;
  const invalidSections = resolveVisibleSections(availableSections, caseState, uiEngineProps).filter(havingErrors).map(s => s.id);
  return { invalidSections };
});

export const validState = (_, uiEngineProps: ConnectedComponentProps) => {
  const {
    validationDescendants,
    hasValidationErrors =
      validationDescendants &&
      validationDescendants.length &&
      validationDescendants.some(v => v.level === ValidationLevel.error),
    hasValidationWarnings =
      validationDescendants &&
      validationDescendants.length &&
      validationDescendants.some(v => v.level === ValidationLevel.warning)
  } = uiEngineProps;

  return {
    valid: !hasValidationErrors,
    warnings: hasValidationWarnings
  };

};
